import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import DownloadIcon from '@mui/icons-material/Download';
import html2pdf from 'html2pdf.js';
import Toast, { useToast } from '../../components/useToast';
import { axiosErrorToUserMessage, axiosIntakeInstance } from '../../services/axios';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { createFirstHxReportHtml, firstHxDocumentStyles } from '../../utils/reportFormatter';

export default function DownloadReportAsPdfButton({
  htmlContent,
  mkey,
  activeSiteId,
  patientID,
  onSuccess,
  customLogoPath,
  siteName,
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const intakeAxiosWithAuth = useAxiosPrivate(axiosIntakeInstance);
  const {
    toastState,
    toastSetSuccessNotification,
    toastSetErrorNotification,
    toastOnClose,
  } = useToast();

  const downloadPdf = async () => {
    try {
      setIsDownloading(true);

      // If html content wasn't provided as a prop, fetch it first
      let reportContent = htmlContent;
      if (!reportContent && mkey && activeSiteId) {
        const response = await intakeAxiosWithAuth.get(`/${activeSiteId}/${mkey}/review`);
        reportContent = response.data.reportHtml;

        if (!reportContent || reportContent.trim() === '') {
          toastSetErrorNotification('No report content available to download');
          setIsDownloading(false);
          return;
        }
      }

      if (!reportContent || reportContent.trim() === '') {
        toastSetErrorNotification('No report content available to download');
        setIsDownloading(false);
        return;
      }
      const reportTitle = patientID ? `Patient Report - ${patientID}` : 'Patient Report';

      const formattedHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${reportTitle}</title>
        <style>
          ${firstHxDocumentStyles}
        </style>
      </head>
      <body>
        ${createFirstHxReportHtml(reportContent, patientID, customLogoPath, siteName)}
      </body>
      </html>
      `;

      // Create a temporary iframe to properly render and isolate the content
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '1024px'; // Fixed size for consistent rendering
      iframe.style.height = '1500px';
      iframe.style.border = 'none';

      document.body.appendChild(iframe);

      // Wait for iframe to load and initialize content
      await new Promise((resolve) => {
        iframe.onload = resolve;
        iframe.srcdoc = formattedHtml;
      });

      // Function to preload all images within the iframe
      const preloadImages = async () => {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
        const images = iframeDocument.querySelectorAll('img');

        if (images.length === 0) return Promise.resolve();

        return Promise.all(Array.from(images).map((image) => {
          if (image.complete) return Promise.resolve();

          return new Promise((resolve) => {
            const imgElement = image;

            const handleLoad = () => {
              resolve();
            };

            const handleError = () => {
              resolve();
            };

            imgElement.onload = handleLoad;
            imgElement.onerror = handleError;
          });
        }));
      };

      // Wait for all images to load
      await preloadImages();

      // Get the document content from the iframe
      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      const contentNode = iframeDocument.documentElement;

      // Configure html2pdf options
      const options = {
        filename: `FirstHx_Report_${patientID || 'Patient'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2, // Higher scale for better quality
          useCORS: true,
          logging: false,
          letterRendering: true,
          allowTaint: true,
          imageTimeout: 15000, // Increased timeout for images
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true,
        },
      };

      // Generate the PDF
      await html2pdf()
        .from(contentNode)
        .set(options)
        .toPdf()
        .get('pdf')
        .then((pdf) => {
          // Add metadata
          pdf.setProperties({
            title: `FirstHx Report - ${patientID || 'Patient'}`,
            subject: 'Patient Intake Report',
            creator: 'FirstHx',
            author: 'FirstHx HxManager',
          });
          return pdf;
        })
        .save();

      // Clean up - remove the iframe after PDF is generated
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 2000);

      // Show success message
      if (onSuccess) {
        onSuccess('Report downloaded as PDF');
      } else {
        toastSetSuccessNotification('Report downloaded as PDF');
      }
    } catch (error) {
      toastSetErrorNotification(
        axiosErrorToUserMessage(error) || 'An error occurred when downloading the PDF.',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Tooltip title="Download as PDF" arrow>
        <IconButton
          onClick={downloadPdf}
          disabled={isDownloading}
          data-testid="downloadPdfIcon"
        >
          <DownloadIcon className="table-tool-icon" />
        </IconButton>
      </Tooltip>
      <Toast
        open={toastState.open}
        severity={toastState.severity}
        text={toastState.text}
        onClose={toastOnClose}
      />
    </>
  );
}

DownloadReportAsPdfButton.propTypes = {
  htmlContent: PropTypes.string,
  mkey: PropTypes.string,
  activeSiteId: PropTypes.string,
  patientID: PropTypes.string,
  onSuccess: PropTypes.func,
  customLogoPath: PropTypes.string,
  siteName: PropTypes.string,
};

DownloadReportAsPdfButton.defaultProps = {
  htmlContent: '',
  mkey: '',
  activeSiteId: '',
  patientID: '',
  onSuccess: null,
  customLogoPath: null,
  siteName: null,
};
